import Fuse from 'fuse.js';
import { searchIndex, searchSynonyms } from '../data/general/searchIndex';

// Configure Fuse.js for optimal fuzzy search
const fuseOptions = {
  // Search in these keys with different weights
  keys: [
    { name: 'title', weight: 0.4 },
    { name: 'subtitle', weight: 0.3 },
    { name: 'description', weight: 0.2 },
    { name: 'content', weight: 0.1 }
  ],
  
  // Fuzzy search settings
  threshold: 0.3, // Lower = more strict, higher = more fuzzy
  distance: 100, // How far apart characters can be for a match
  
  // Typo tolerance
  minMatchCharLength: 2, // Minimum characters that must match
  
  // Advanced options
  includeScore: true, // Include relevance score
  includeMatches: true, // Include which parts matched
  
  // Ignore case and accents
  ignoreLocation: true,
  useExtendedSearch: false,
  
  // Performance
  findAllMatches: true,
  shouldSort: true
};

// Initialize Fuse instance
const fuse = new Fuse(searchIndex, fuseOptions);

/**
 * Enhanced search function with fuzzy matching and typo tolerance
 * @param {string} query - Search query
 * @param {string} category - Optional category filter
 * @returns {Array} - Sorted search results
 */
export const performEnhancedSearch = (query, category = 'All') => {
  if (!query.trim()) {
    return [];
  }

  const searchQuery = query.toLowerCase().trim();
  
  // Check for synonyms and expand the search
  let expandedQuery = searchQuery;
  if (searchSynonyms[searchQuery]) {
    expandedQuery = `${searchQuery} ${searchSynonyms[searchQuery]}`;
  }

  // Perform fuzzy search
  const fuseResults = fuse.search(expandedQuery);
  
  // Process and enhance results
  const enhancedResults = fuseResults.map(result => {
    const item = result.item;
    const score = result.score || 1;
    
    // Calculate relevance score (0 = perfect match, 1 = no match)
    let relevanceScore = score;
    
    // Boost exact matches
    if (item.title.toLowerCase().includes(searchQuery)) {
      relevanceScore *= 0.5;
    }
    
    // Boost partial word matches
    const words = searchQuery.split(' ');
    const titleWords = item.title.toLowerCase().split(' ');
    const matchingWords = words.filter(word => 
      titleWords.some(titleWord => titleWord.includes(word))
    );
    if (matchingWords.length > 0) {
      relevanceScore *= (0.8 ** matchingWords.length);
    }
    
    // Boost priority items
    if (item.priority === 1) {
      relevanceScore *= 0.9;
    }
    
    // Boost category matches
    if (category !== 'All' && item.category === category) {
      relevanceScore *= 0.85;
    }
    
    return {
      ...item,
      relevanceScore: Math.max(0, Math.min(1, relevanceScore)),
      originalScore: score,
      matchedWords: matchingWords
    };
  });

  // Filter by category if specified
  const filteredResults = category === 'All' 
    ? enhancedResults 
    : enhancedResults.filter(result => result.category === category);

  // Sort by relevance score (best matches first)
  const sortedResults = filteredResults.sort((a, b) => {
    // First by relevance score
    if (a.relevanceScore !== b.relevanceScore) {
      return a.relevanceScore - b.relevanceScore;
    }
    
    // Then by priority
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    
    // Finally by title alphabetical order
    return a.title.localeCompare(b.title);
  });

  // Limit results to top 20 for performance
  return sortedResults.slice(0, 20);
};

/**
 * Get search suggestions based on partial query
 * @param {string} query - Partial search query
 * @returns {Array} - Array of suggestion strings
 */
export const getSearchSuggestions = (query) => {
  if (!query || !query.trim() || query.length < 2 || !searchIndex) {
    return [];
  }

  const suggestions = new Set();
  const searchQuery = query.toLowerCase();

  // Find matching titles and descriptions
  searchIndex.forEach(item => {
    if (item && item.title && typeof item.title === 'string' && item.title.toLowerCase().includes(searchQuery)) {
      suggestions.add(item.title);
    }
    if (item && item.subtitle && typeof item.subtitle === 'string' && item.subtitle.toLowerCase().includes(searchQuery)) {
      suggestions.add(item.subtitle);
    }
  });

  // Add synonym-based suggestions
  if (searchSynonyms && typeof searchSynonyms === 'object') {
    Object.keys(searchSynonyms).forEach(synonym => {
      if (synonym && typeof synonym === 'string' && synonym.includes(searchQuery)) {
        suggestions.add(synonym);
      }
    });
  }

  // Convert to array and limit results
  return Array.from(suggestions).slice(0, 8);
};

/**
 * Get popular search terms based on content
 * @returns {Array} - Array of popular search terms
 */
export const getPopularSearches = () => {
  return [
    'Shabbat',
    'Kosher',
    'FYSHH',
    'Events',
    'Staff',
    'Student Groups',
    'Jewish Learning',
    'Parents',
    'Alumni',
    'Room Reservation'
  ];
};

/**
 * Highlight search terms in text
 * @param {string} text - Text to highlight
 * @param {string} query - Search query
 * @returns {string} - Text with highlighted terms
 */
export const highlightSearchTerms = (text, query) => {
  if (!query || !text) return text;
  
  const words = query.toLowerCase().split(' ');
  let highlightedText = text;
  
  words.forEach(word => {
    if (word.length > 2) {
      const regex = new RegExp(`(${word})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
    }
  });
  
  return highlightedText;
};

/**
 * Get search result icon based on type
 * @param {string} type - Result type
 * @returns {string} - Emoji icon
 */
export const getSearchResultIcon = (type) => {
  const iconMap = {
    'staff': '👥',
    'student-group': '👥',
    'fellowship': '🎓',
    'holiday': '⭐',
    'parent-resource': '👨‍👩‍👧‍👦',
    'student-type': '🎓',
    'faq': '❓',
    'facility': '🏢',
    'religious-life': '✡️',
    'travel': '✈️',
    'internship': '💼',
    'kosher-soup': '🥣',
    'page': 'ℹ️'
  };
  
  return iconMap[type] || '🔍';
};

const searchUtils = {
  performEnhancedSearch,
  getSearchSuggestions,
  getPopularSearches,
  highlightSearchTerms,
  getSearchResultIcon
};

export default searchUtils;

